/**
 * Real-Time Notifications Constants
 *
 * This file defines all constants used in real-time notifications and watch list functionality.
 * All components should use these constants instead of hardcoded values.
 */

// Polling intervals (in milliseconds)
export const POLLING_INTERVALS = {
  /** Real-time notifications polling interval (15 seconds) */
  REAL_TIME_NOTIFICATIONS: 15000,
  /** Legacy polling interval (30 seconds) - used in other components */
  LEGACY: 30000,
} as const;

// Backoff configuration for polling
export const BACKOFF_CONFIG = {
  /** Initial backoff interval (in milliseconds) */
  INITIAL_INTERVAL: 15000,
  /** Maximum backoff interval (in milliseconds) */
  MAX_INTERVAL: 300000, // 5 minutes
  /** Backoff multiplier */
  MULTIPLIER: 2,
  /** Maximum number of consecutive failures before max backoff */
  MAX_FAILURES: 5,
} as const;

// Watch List limits and defaults
export const WATCH_LIST = {
  /** Fallback limit when backend doesn't provide one */
  FALLBACK_LIMIT: 25,
} as const;

// Time intervals (in milliseconds)
export const TIME_INTERVALS = {
  /** 1 second in milliseconds */
  ONE_SECOND: 1000,
  /** 1 minute in milliseconds */
  ONE_MINUTE: 60000,
  /** 15 seconds in milliseconds */
  FIFTEEN_SECONDS: 15000,
  /** 30 seconds in milliseconds */
  THIRTY_SECONDS: 30000,
} as const;

// API endpoints
export const API_ENDPOINTS = {
  /** User profile endpoint */
  USER_PROFILE: 'user_profile/user/current_profile/',
  /** Watched certifications endpoint */
  WATCHED_CERTIFICATIONS: 'user_profile/watched_certifications/for_user/',
  /** Email notifications endpoint */
  EMAIL_NOTIFICATIONS: 'email_notifications/email_notifications/',
} as const;

// Error messages
export const ERROR_MESSAGES = {
  /** Failed to load watched certifications */
  FAILED_TO_LOAD_WATCHED_CERTS: 'Failed to load watched certifications.',
  /** Failed to add to watch list */
  FAILED_TO_ADD_TO_WATCH_LIST: 'Failed to add to watch list',
  /** Failed to remove from watch list */
  FAILED_TO_REMOVE_FROM_WATCH_LIST: 'Failed to remove from watch list',
  /** Failed to mark notification as read */
  FAILED_TO_MARK_AS_READ: 'Failed to mark notification as read',
  /** Failed to mark notification as unread */
  FAILED_TO_MARK_AS_UNREAD: 'Failed to mark notification as unread',
} as const;

// API query parameters
export const API_QUERY_PARAMS = {
  /** Expand parameters for watched certifications */
  WATCHED_CERTS_EXPAND:
    'property,household,category,certification_config,compliance_user',
  /** Fields for watched certifications */
  WATCHED_CERTS_FIELDS:
    'id,display_name,effective_date,deadline,property.name,property.slug,household.id,household.name,category.id,category.name,certification_config.id,certification_config.name,compliance_user.id,compliance_user.first_name,compliance_user.last_name',
} as const;

export type PollingInterval = keyof typeof POLLING_INTERVALS;
export type WatchListLimit = keyof typeof WATCH_LIST;
export type TimeInterval = keyof typeof TIME_INTERVALS;
export type ApiEndpoint = keyof typeof API_ENDPOINTS;
export type ErrorMessage = keyof typeof ERROR_MESSAGES;
export type ApiQueryParam = keyof typeof API_QUERY_PARAMS;
